import { useState, useEffect, useMemo } from "react";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";
import { formatNumber } from "../../../utils/formatNumber";
import { useAuth } from "../../../contexts/AuthContext";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import { getSaleItemSummary } from "../../../utils/salePricing";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const TAX_RATE = 0.07;

const STATUS_OPTIONS = [
  { label: "ส่งแล้ว", value: "sent" },
  { label: "ยืนยันแล้ว", value: "confirm" },
  { label: "รับเงินแล้ว", value: "payed" },
];

const mapPersonType = {
  normal: "บุคคลธรรมดา",
  company: "นิติบุคคล",
};

export default function SalesByCustomerSummary() {
  const auth = useAuth();

  const formatDate = (date) => date.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return formatDate(d);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [loading, setLoading] = useState(false);

  // data
  const [rows, setRows] = useState([]);
  const [openSet, setOpenSet] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(STATUS_OPTIONS[0].value);

  // sort main
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");

  // inventory avg cost
  const [invById, setInvById] = useState({});
  const [invByName, setInvByName] = useState({});

  const canSeeCost = useMemo(() => {
    if (!auth) return false;
    const perm = auth?.permissions ?? {};
    return ["report_show_cost"].some((k) => perm?.[k] === true);
  }, [auth]);

  const canSeeProfit = useMemo(() => {
    if (!auth) return false;
    const perm = auth?.permissions ?? {};
    return ["report_show_profit"].some((k) => perm?.[k] === true);
  }, [auth]);

  // ---------- Inventory (avg cost) ----------
  useEffect(() => {
    const loadInventory = async () => {
      try {
        const invRes = await services.getInventory();
        const invRaw = invRes?.data?.data ?? invRes?.data ?? invRes ?? [];
        const _byId = {};
        const _byName = {};
        (Array.isArray(invRaw) ? invRaw : [invRaw]).forEach((rec) => {
          const avg =
            Number(
              rec?.avgCostPerUnit ??
                rec?.averageCost ??
                rec?.avgCost ??
                rec?.costPerUnit ??
                rec?.cost ??
                0
            ) || 0;

          const pid =
            rec?.productId ??
            rec?.product?.id ??
            rec?.id ??
            rec?.product_id ??
            null;

          const pname =
            rec?.productName ??
            rec?.product?.title ??
            rec?.name ??
            rec?.product_name ??
            null;

          if (pid != null)
            _byId[String(pid)] = {
              avgCostPerUnit: avg,
              name: pname ?? undefined,
            };
          if (pname)
            _byName[String(String(pname).trim())] = {
              avgCostPerUnit: avg,
              id: pid ?? undefined,
            };
        });

        setInvById(_byId);
        setInvByName(_byName);
      } catch (e) {
        console.error("loadInventory error", e);
        setInvById({});
        setInvByName({});
      }
    };

    loadInventory();
  }, []);

  // ---- fetch sale transactions (respect status) ----
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getSaleTransactions({
          ...(selectedStatus ? { status: selectedStatus } : {}),
          startDate,
          endDate,
        });
        const raw = res?.data?.data ?? res?.data ?? res ?? [];
        const grouped = groupByCustomer(raw, invById, invByName);
        setRows(grouped);
        setOpenSet(new Set());
      } catch (e) {
        console.error("fetch error", e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate, selectedStatus, invById, invByName]);

  // ---- filter ----
  const filteredRows = useMemo(() => {
    const q = (search || "").toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const name = String(row.customer?.name ?? "").toLowerCase();
      const type = String(row.customer?.personType ?? "").toLowerCase();
      const cars = (row.carNames || []).join(" ").toLowerCase();
      return name.includes(q) || type.includes(q) || cars.includes(q);
    });
  }, [rows, search]);

  // ---- sort (main) ----
  const getRowMetrics = (row) => {
    const totalSend = sumCustom(row.items, (i) =>
      num(i.sendWeight ?? i.netWeight)
    );
    const totalDest = sum(row.items, "netWeight");
    const totalDeduct = totalSend - totalDest;
    const totalBefore = sum(row.items, "amountBeforeVat");
    const totalVat = sum(row.items, "vat");
    const totalNet = sum(row.items, "netPrice");
    const totalCost = sum(row.items, "costAmount");

    const totalProfitBeforeVat = totalBefore - totalCost; // กำไรก่อนภาษี
    const totalProfit = totalNet - totalCost; // กำไร (รวม VAT)

    const unitCount = (row.items || []).length;

    const avgCostPerUnit = totalDest > 0 ? totalCost / totalDest : 0;

    return {
      totalSend,
      totalDest,
      totalDeduct,
      totalBefore,
      totalVat,
      totalNet,
      totalCost,
      totalProfitBeforeVat,
      totalProfit,
      unitCount,
      avgCostPerUnit,
      name: row.customer?.name ?? "",
      personType: row.customer?.personType ?? "",
    };
  };

  const sortedRows = useMemo(() => {
    const data = [...filteredRows];
    data.sort((a, b) => {
      const mA = getRowMetrics(a);
      const mB = getRowMetrics(b);

      let aVal, bVal;

      switch (orderBy) {
        case "name":
          aVal = String(mA.name).toLowerCase();
          bVal = String(mB.name).toLowerCase();
          break;
        case "personType":
          aVal = String(mA.personType).toLowerCase();
          bVal = String(mB.personType).toLowerCase();
          break;
        case "totalSend":
          aVal = mA.totalSend;
          bVal = mB.totalSend;
          break;
        case "totalDeduct":
          aVal = mA.totalDeduct;
          bVal = mB.totalDeduct;
          break;
        case "totalDest":
          aVal = mA.totalDest;
          bVal = mB.totalDest;
          break;
        case "totalBefore":
          aVal = mA.totalBefore;
          bVal = mB.totalBefore;
          break;
        case "totalVat":
          aVal = mA.totalVat;
          bVal = mB.totalVat;
          break;
        case "totalNet":
          aVal = mA.totalNet;
          bVal = mB.totalNet;
          break;
        case "totalCost":
          aVal = mA.totalCost;
          bVal = mB.totalCost;
          break;
        case "totalProfit":
          aVal = mA.totalProfit;
          bVal = mB.totalProfit;
          break;
        case "unitCount":
          aVal = mA.unitCount;
          bVal = mB.unitCount;
          break;
        default:
          aVal = 0;
          bVal = 0;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        const cmp = aVal.localeCompare(bVal, "th");
        return order === "asc" ? cmp : -cmp;
      } else {
        const cmp = (aVal || 0) - (bVal || 0);
        return order === "asc" ? cmp : -cmp;
      }
    });
    return data;
  }, [filteredRows, orderBy, order]);

  const requestSort = (key) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(key);
      setOrder(["name", "personType"].includes(key) ? "asc" : "desc");
    }
  };

  // ---- summary footer (ไม่ต้องพึ่ง sort) ----
  const summary = useMemo(() => {
    let totalSend = 0,
      totalDeduct = 0,
      totalDest = 0,
      totalBefore = 0,
      totalVat = 0,
      totalNet = 0,
      totalCost = 0,
      totalProfit = 0;

    filteredRows.forEach((r) => {
      (r.items || []).forEach((i) => {
        const sendW = num(i.sendWeight ?? i.netWeight);
        const destW = num(i.netWeight);
        totalSend += sendW;
        totalDest += destW;
        totalDeduct += sendW - destW;

        totalBefore += num(i.amountBeforeVat);
        totalVat += num(i.vat);
        totalNet += num(i.netPrice);
        totalCost += num(i.costAmount);
        totalProfit += num(i.amountBeforeVat) - num(i.costAmount);
      });
    });

    return {
      totalSend,
      totalDeduct,
      totalDest,
      totalBefore,
      totalVat,
      totalNet,
      totalCost,
      totalProfit,
      customerCount: filteredRows.length,
    };
  }, [filteredRows]);

  const getRowKey = (row, idx) =>
    String(row.customerId ?? row.customer?.id ?? idx);

  const toggleOpen = (key) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ===== Exports =====
  const exportToCSV = (data, filename = "export.csv") => {
    if (!data || !data.length) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportSummary = () => {
    const dataset = sortedRows.map((row) => {
      const m = getRowMetrics(row);
      return {
        รายการ: row.customer?.name ?? "-",
        ประเภทลูกค้า: mapPersonType[row.customer?.personType] ?? "-",
        รถขนส่ง: (row.carNames || []).join(" | "),
        น้ำหนักส่งสุทธิ: fix2(m.totalSend),
        น้ำหนักหัก: fix2(m.totalDeduct),
        น้ำหนักปลายทางสุทธิ: fix2(m.totalDest),
        หน่วย: m.unitCount, // จำนวน sub rows
        ยอดก่อนรวมภาษี: fix2(m.totalBefore),
        ยอดภาษี: fix2(m.totalVat),
        ยอดสุทธิ: fix2(m.totalNet),
        ต้นทุน: fix2(m.totalCost),
        กำไร: fix2(m.totalProfit),
      };
    });
    exportToCSV(dataset, `sales_by_customer_${startDate}_to_${endDate}.csv`);
  };

  const handleExportAllItems = () => {
    const rowsFlat = [];
    sortedRows.forEach((row) => {
      (row.items || []).forEach((p) => {
        const sendW = num(p.sendWeight ?? p.netWeight);
        const destW = num(p.netWeight);
        const deductW = sendW - destW;
        const amountBefore = num(p.amountBeforeVat);
        const vat = num(p.vat);
        const total = amountBefore + vat;
        const costAmount = num(p.costAmount);
        const profit = amountBefore - costAmount;

        rowsFlat.push({
          ลูกค้า: row.customer?.name ?? "-",
          ประเภทลูกค้า: mapPersonType[row.customer?.personType] ?? "-",
          สินค้า: p.product?.title ?? p.productName ?? "-",
          น้ำหนักส่งสุทธิ: fix2(sendW),
          น้ำหนักหัก: fix2(deductW),
          น้ำหนักปลายทางสุทธิ: fix2(destW),
          หน่วย: p.unit || "กก.",
          ยอดก่อนรวมภาษี: fix2(amountBefore),
          ยอดภาษี: fix2(vat),
          ยอดสุทธิ: fix2(total),
          ต้นทุน: fix2(costAmount),
          กำไร: fix2(profit),
        });
      });
    });
    exportToCSV(rowsFlat, `sales_items_${startDate}_to_${endDate}.csv`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <AppBreadcrumbs />
        </div>

        {/* Header & Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-2 xl:mb-0">
               <span className="w-1 h-8 bg-green-600 rounded-full"></span>
               สรุปยอดขายสินค้ารายลูกค้า
            </h1>

            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 w-full xl:w-auto">
                {/* Search */}
               <div className="relative w-full lg:w-64">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="ชื่อลูกค้า / ประเภท / ทะเบียนรถ"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                />
               </div>

                {/* Status Toggle */}
               <div className="flex bg-gray-100 p-1 rounded-xl">
                 {STATUS_OPTIONS.map((opt) => (
                   <button
                     key={opt.value}
                     onClick={() => setSelectedStatus(opt.value)}
                     className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                       selectedStatus === opt.value
                         ? "bg-white text-green-700 shadow-sm"
                         : "text-gray-500 hover:text-gray-700"
                     }`}
                   >
                     {opt.label}
                   </button>
                 ))}
               </div>

              {/* Date Pickers */}
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200 w-full lg:w-auto">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">เริ่ม</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm w-full"
                />
              </div>

               <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200 w-full lg:w-auto">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">สิ้นสุด</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm w-full"
                />
              </div>
            </div>
          </div>
          
           <div className="mt-6 flex gap-2 justify-end">
                 <button
                    onClick={handleExportSummary}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-medium shadow-sm"
                >
                    <ContentCopyIcon fontSize="small" />
                    <span>Export ข้อมูลหลัก</span>
                </button>
                 <button
                    onClick={handleExportAllItems}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white border border-transparent rounded-xl hover:bg-green-700 transition-all font-medium shadow-sm hover:shadow-md"
                >
                    <ContentCopyIcon fontSize="small" />
                    <span>Export ข้อมูลย่อยทั้งหมด</span>
                </button>
          </div>
        </div>

        {/* Content */}
         {loading ? (
             <div className="flex justify-center items-center h-64">
                <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
             </div>
        ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                 <div className="overflow-x-auto">
                     <table className="w-full text-sm text-left">
                         <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-100">
                             <tr>
                                <th className="px-4 py-4 w-12"></th>
                                <th onClick={() => requestSort("name")} className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center gap-1">
                                        รายการ {orderBy === "name" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("personType")} className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center gap-1">
                                        ประเภทลูกค้า {orderBy === "personType" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th className="px-4 py-4">รถขนส่ง</th>
                                <th onClick={() => requestSort("totalSend")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        น้ำหนักส่ง {orderBy === "totalSend" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("totalDeduct")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        หัก {orderBy === "totalDeduct" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("totalDest")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        สุทธิ {orderBy === "totalDest" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th className="px-4 py-4">หน่วย</th>
                                <th onClick={() => requestSort("totalBefore")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        ก่อนภาษี {orderBy === "totalBefore" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("totalVat")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        ภาษี {orderBy === "totalVat" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("totalNet")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        ยอดสุทธิ {orderBy === "totalNet" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                {canSeeCost && (
                                     <th onClick={() => requestSort("totalCost")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                        <div className="flex items-center justify-end gap-1">
                                            ต้นทุน {orderBy === "totalCost" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                        </div>
                                    </th>
                                )}
                                {canSeeProfit && (
                                     <th onClick={() => requestSort("totalProfit")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                        <div className="flex items-center justify-end gap-1">
                                            กำไร {orderBy === "totalProfit" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                        </div>
                                    </th>
                                )}
                             </tr>
                         </thead>
                         <tbody className="divide-y divide-gray-50">
                             {sortedRows.map((row, idx) => {
                                 const key = getRowKey(row, idx);
                                 const isOpen = openSet.has(key);
                                 const m = getRowMetrics(row);
                                 return (
                                     <RowGroup
                                        key={key}
                                        isOpen={isOpen}
                                        onToggle={() => toggleOpen(key)}
                                        row={row}
                                        metrics={m}
                                        canSeeCost={canSeeCost}
                                        canSeeProfit={canSeeProfit}
                                        formatNumber={formatNumber}
                                     />
                                 );
                             })}
                         </tbody>
                         <tfoot className="bg-gray-50 font-bold text-gray-900 border-t border-gray-200">
                             <tr>
                                 <td colSpan={4} className="px-4 py-4 text-right">
                                    รวมลูกค้า {summary.customerCount.toLocaleString("th-TH")} ราย
                                 </td>
                                 <td className="px-4 py-4 text-right">{formatNumber(summary.totalSend)}</td>
                                 <td className="px-4 py-4 text-right">{formatNumber(summary.totalDeduct)}</td>
                                 <td className="px-4 py-4 text-right">{formatNumber(summary.totalDest)}</td>
                                 <td className="px-4 py-4 pl-4">
                                     {filteredRows.reduce((acc, r) => acc + (r.items?.length || 0), 0)}
                                 </td>
                                 <td className="px-4 py-4 text-right">{formatNumber(summary.totalBefore)}</td>
                                 <td className="px-4 py-4 text-right">{formatNumber(summary.totalVat)}</td>
                                 <td className="px-4 py-4 text-right text-green-700 text-lg">{formatNumber(summary.totalNet)}</td>
                                 {canSeeCost && <td className="px-4 py-4 text-right">{formatNumber(summary.totalCost)}</td>}
                                 {canSeeProfit && <td className="px-4 py-4 text-right">{formatNumber(summary.totalProfit)}</td>}
                             </tr>
                         </tfoot>
                     </table>
                 </div>
            </div>
        )}
      </div>
    </div>
  );
}

function RowGroup({ isOpen, onToggle, row, metrics, canSeeCost, canSeeProfit, formatNumber }) {
    return (
        <>
            <tr className="hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-none">
                 <td className="px-4 py-4">
                    <button 
                        onClick={onToggle}
                        className="p-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500"
                    >
                        {isOpen ? <KeyboardArrowDownIcon fontSize="small"/> : <KeyboardArrowRightIcon fontSize="small"/>}
                    </button>
                </td>
                <td className="px-4 py-4 font-medium text-gray-800">{row.customer?.name ?? "-"}</td>
                <td className="px-4 py-4 text-gray-600">
                    {mapPersonType[row.customer?.personType] ?? "-"}
                </td>
                <td className="px-4 py-4">
                     <div className="flex flex-wrap gap-1">
                        {(row.carNames || []).map((car, i) => (
                            <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                                {car}
                            </span>
                        ))}
                    </div>
                </td>
                <td className="px-4 py-4 text-right font-medium">{formatNumber(metrics.totalSend)}</td>
                <td className="px-4 py-4 text-right text-gray-500">{formatNumber(metrics.totalDeduct)}</td>
                <td className="px-4 py-4 text-right font-medium text-green-600">{formatNumber(metrics.totalDest)}</td>
                <td className="px-4 py-4 text-gray-500 text-sm">{metrics.unitCount}</td>
                <td className="px-4 py-4 text-right text-gray-600">{formatNumber(metrics.totalBefore)}</td>
                <td className="px-4 py-4 text-right text-gray-600">{formatNumber(metrics.totalVat)}</td>
                <td className="px-4 py-4 text-right font-bold text-gray-800">{formatNumber(metrics.totalNet)}</td>
                {canSeeCost && (
                     <td className="px-4 py-4 text-right text-gray-500">{formatNumber(metrics.totalCost)}</td>
                )}
                {canSeeProfit && (
                     <td className={`px-4 py-4 text-right font-medium ${
                        metrics.totalProfit > 0 ? 'text-green-600' : metrics.totalProfit < 0 ? 'text-red-500' : 'text-gray-600'
                     }`}>
                        {formatNumber(metrics.totalProfit)}
                    </td>
                )}
            </tr>

            {isOpen && (
                <tr className="bg-gray-50/50">
                    <td colSpan={canSeeCost && canSeeProfit ? 13 : canSeeCost || canSeeProfit ? 12 : 11} className="px-4 py-4">
                         <div className="pl-12 pr-4">
                             <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                                <span className="w-1 h-4 bg-green-500 rounded-full"></span>
                                รายการสินค้า
                            </h4>
                             <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                 <table className="w-full text-sm text-left">
                                     <thead className="bg-gray-50 text-gray-500 border-b border-gray-200">
                                         <tr>
                                            <th className="px-4 py-2 font-medium">รายการ</th>
                                            <th className="px-4 py-2 text-right font-medium">น้ำหนักส่ง</th>
                                            <th className="px-4 py-2 text-right font-medium">หัก</th>
                                            <th className="px-4 py-2 text-right font-medium">สุทธิ</th>
                                            <th className="px-4 py-2 font-medium">หน่วย</th>
                                            <th className="px-4 py-2 text-right font-medium">ก่อนภาษี</th>
                                            <th className="px-4 py-2 text-right font-medium">ภาษี</th>
                                            <th className="px-4 py-2 text-right font-medium">ยอดรวม</th>
                                            {canSeeCost && <th className="px-4 py-2 text-right font-medium">ต้นทุน</th>}
                                            {canSeeProfit && <th className="px-4 py-2 text-right font-medium">กำไร</th>}
                                         </tr>
                                     </thead>
                                     <tbody className="divide-y divide-gray-100">
                                         {(row.items || []).map((p, i) => {
                                             const sendW = Number(p.sendWeight ?? p.netWeight ?? 0);
                                             const destW = Number(p.netWeight ?? 0);
                                             const deductW = sendW - destW;
                                             const amountBefore = Number(p.amountBeforeVat ?? destW * Number(p.pricePerUnit ?? 0));
                                             const vat = Number(p.vat ?? amountBefore * TAX_RATE);
                                             const total = amountBefore + vat;
                                             const costAmount = Number(p.costAmount ?? 0);
                                             const profit = amountBefore - costAmount;

                                             return (
                                                 <tr key={i} className="hover:bg-gray-50">
                                                     <td className="px-4 py-2 text-gray-800">{p.product?.title ?? p.productName ?? "-"}</td>
                                                     <td className="px-4 py-2 text-right text-gray-600">{formatNumber(sendW)}</td>
                                                     <td className="px-4 py-2 text-right text-gray-600">{formatNumber(deductW)}</td>
                                                     <td className="px-4 py-2 text-right font-medium text-gray-800">{formatNumber(destW)}</td>
                                                     <td className="px-4 py-2 text-gray-500">{p.unit || "กก."}</td>
                                                     <td className="px-4 py-2 text-right text-gray-600">{formatNumber(amountBefore)}</td>
                                                     <td className="px-4 py-2 text-right text-gray-600">{formatNumber(vat)}</td>
                                                     <td className="px-4 py-2 text-right font-medium text-gray-800">{formatNumber(total)}</td>
                                                     {canSeeCost && (
                                                         <td className="px-4 py-2 text-right text-gray-600">{formatNumber(costAmount)}</td>
                                                     )}
                                                     {canSeeProfit && (
                                                         <td className={`px-4 py-2 text-right font-medium ${profit > 0 ? 'text-green-600' : profit < 0 ? 'text-red-500' : 'text-gray-600'}`}>
                                                             {formatNumber(profit)}
                                                         </td>
                                                     )}
                                                 </tr>
                                             );
                                         })}
                                     </tbody>
                                 </table>
                             </div>
                         </div>
                    </td>
                </tr>
            )}
        </>
    );
}

// Helpers
function groupByCustomer(data = [], invById = {}, invByName = {}) {
  const grouped = {};

  for (const row of data) {
    const cid = row.customerId ?? row.customer?.id ?? "__unknown__";
    if (!grouped[cid]) {
      grouped[cid] = {
        customerId: cid,
        customer: row.customer ?? { id: cid, name: "-" },
        carNames: [],
        items: [],
      };
    }

    const carName = row.car?.license
      ? `${row.car?.type || ""} ${row.car.license}`.trim()
      : "ไม่มีข้อมูล";
    if (carName && !grouped[cid].carNames.includes(carName)) {
      grouped[cid].carNames.push(carName);
    }

    const isVat = row.isVat === true;

    const items = (row.items ?? []).map((it) => {
      const saleSummary = getSaleItemSummary(it);
      const destW = saleSummary.weight;
      const sendW = num(it.sendWeight ?? destW);

      const pricePerUnit = saleSummary.pricePerUnit;
      const amountBefore = saleSummary.amount;
      const vat = isVat ? num(it.vat ?? amountBefore * TAX_RATE) : 0;
      const netPrice = amountBefore + vat;

      // ---- cost lookup from inventory ----
      const pid = it.productId ?? it.product?.id ?? null;
      const pname = String(it.productName ?? it.product?.title ?? "-").trim();

      const invCostById =
        pid != null ? invById[String(pid)]?.avgCostPerUnit : undefined;
      const invCostByName = pname
        ? invByName[pname]?.avgCostPerUnit
        : undefined;

      const costPerUnit = num(
        it.costPerUnit ?? invCostById ?? invCostByName ?? 0
      );
      const costAmount = num(it.costAmount ?? costPerUnit * destW);

      return {
        ...it,
        productName: pname,
        unit: it.unit || "กก.",
        sendWeight: sendW,
        netWeight: destW,
        pricePerUnit,
        amountBeforeVat: amountBefore,
        vat,
        netPrice,
        costPerUnit,
        costAmount,
      };
    });

    grouped[cid].items.push(...items);
  }

  return Object.values(grouped).map((entry) => {
    const merged = {};
    for (const it of entry.items || []) {
      const key = it.product?.title ?? it.productName ?? "-";
      if (!merged[key]) {
        merged[key] = { ...it };
      } else {
        merged[key].sendWeight += num(it.sendWeight);
        merged[key].netWeight += num(it.netWeight);
        merged[key].amountBeforeVat += num(it.amountBeforeVat);
        merged[key].vat += num(it.vat);
        merged[key].netPrice += num(it.netPrice);
        merged[key].costAmount += num(it.costAmount);
      }
    }
    return { ...entry, items: Object.values(merged) };
  });
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function sum(arr, key) {
  return (arr || []).reduce((a, x) => a + num(x?.[key]), 0);
}
function sumCustom(arr, fn) {
  return (arr || []).reduce((a, x) => a + num(fn(x)), 0);
}
function fix2(v) {
  return Number(num(v)).toFixed(2);
}
